import React, { useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import { getOptions } from "utils/fields";
import {
    getCoolingSystemTemplate,
    getEStarOptions,
    getEStarManufacturerOptions,
    getEStarModelOptions,
} from "utils/mechanicals";
import { getValidation } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import Select from "components/Input/Select";
import Button from "components/Button";
import Drawer from "components/Drawer";
import AddIcon from "assets/images/icons/JSX/Add";
import AirConditioning from "./AirConditioning";
import AirHeatPump from "./AirHeatPump";
import WaterGroundHeatPump from "./WaterGroundHeatPump";
import FansAndPumps from "./FansAndPumps";
import AddBox from "components/AddBox";
import Actions from "./Actions";
import Accordion from "components/Accordion";

import sharedClasses from "features/Model/sharedStyles.module.scss";
import classes from "../style.module.scss";
import { removeFormErrors } from "store/form/actions";

const primaryCoolingValidation = getValidation("coolingPrimarySelected");
const eStarManufacturerValidation = getValidation("coolingEStarManufacturer");
const eStarModelValidation = getValidation("coolingEStarModel");
const seasonStartValidation = getValidation("coolingSeasonStart");
const seasonEndValidation = getValidation("coolingSeasonEnd");
const seasonDesignValidation = getValidation("coolingSeasonDesign");

const typeContent = (type) =>
    ({
        airConditioning: {
            name: "Air Conditioning",
            Component: AirConditioning,
        },
        airHeatPump: {
            name: "Air Source Heat Pump",
            Component: AirHeatPump,
        },
        waterHeatPump: {
            name: "Water Source Heat Pump",
            Component: WaterGroundHeatPump,
        },
        groundHeatPump: {
            name: "Ground Source Heat Pump",
            Component: WaterGroundHeatPump,
        },
    })[type];

const coolingSystemAddOns = ({ type, h2kMinor }) => {
    if (h2kMinor >= 11 && type !== "airConditioning") {
        return {
            ahri: 0,
            ccashp: false,
            numberOfHeads: 1,
            performanceCurve:
                type === "airHeatPump"
                    ? {
                          cooling: {
                              highCooling: { temperature: 35 }, //95F
                              lowCooling: { temperature: 27.778 }, //82F
                          },
                          heating: {
                              highHeating: { temperature: 8.333 }, // 47F
                              midHeating: { temperature: -8.333 }, // 17F
                              lowHeating: { temperature: -15 }, // 5F
                          },
                      }
                    : {},
        };
    }
    return {};
};

const CoolingHeatPumps = ({
    accessor,
    favId,
    change,
    isUpgrade = false,
    formName,
    canBeEditedFromParametric = true,
    isParametricAnalysis,
    isCommunity = false,
    removeFormErrors,
}) => {
    const [open, toggleOpen] = useState(false);

    const accessorForModelDataFromParametric = `parametricAnalysis.parametricAnalysis.model.${accessor}`;

    let { major: h2kMajor, minor: h2kMinor } =
        useStateSelector(
            isParametricAnalysis
                ? `parametricAnalysis.parametricAnalysis.model.modelData.h2kVersion`
                : `model.modelData.h2kVersion`
        ) || {};

    if (isCommunity) {
        h2kMinor = 12;
    }

    const currentPrimarySystem = Object.keys(useStateSelector(`${accessor}.cooling.system`) || {})[0];
    const primarySystemType = useStateSelector(`${accessor}.cooling.selected.type`);
    const eStarManufacturerValue = useStateSelector(`${accessor}.cooling.selected.eStar.manufacturer`);
    const eStarModelId = useStateSelector(`${accessor}.cooling.selected.eStar.modelId`);

    const currentHeatingSystem = Object.keys(useStateSelector(`${accessor}.heating.system`) || {})[0];

    const fansAndPumpsObj = {
        flowRate: 0,
        hasEnergyEfficientMotor: false,
        mode: {
            id: 0,
        },
        power: {
            isCalculated: true,
            value: null,
        },
    };

    const handleCreateSystem = ({ type, manufacturer, modelId, favId, h2kMinor }) => {
        removeFormErrors(`${accessor}.cooling.system`);
        let coolingTemplate = {};

        if (favId) {
            //Favourite System
        } else if (manufacturer && modelId) {
            //EStar
            coolingTemplate = getEStarOptions(type)[modelId] || {};
            //Hangle GSHP case
            coolingTemplate =
                type === "groundHeatPump" ? { groundHeatPump: coolingTemplate["waterHeatPump"] } : coolingTemplate;

            if (type === "airHeatPump") {
                coolingTemplate[type].specifications.heatingEfficiency.isCop = false;
                coolingTemplate[type].specifications.coolingEfficiency.isCop = false;
            }
        } else {
            //Create standard system from template
            coolingTemplate = getCoolingSystemTemplate(type) || {};
        }

        if (isParametricAnalysis || isUpgrade) {
            coolingTemplate[type] = {
                ...coolingTemplate[type],
                specifications: {
                    ...coolingTemplate[type].specifications,
                    outputCapacity: {
                        id: 0, //always calculated for parametric analysis or upgrade
                        value: 7,
                        uiUnits: "kW",
                    },
                },
            };
        }

        change(`${accessor}.cooling.system`, {
            [type]: {
                ...coolingTemplate[type],
                ...coolingSystemAddOns({ type, h2kMinor }),
            },
        });
        change(`${accessor}.cooling.fansAndPumps`, fansAndPumpsObj);

        if (type.includes("HeatPump") && ["furnace", "boiler"].includes(currentHeatingSystem)) {
            change(`${accessor}.heating.system.${currentHeatingSystem}.isBiEnergy`, false);
            change(`${accessor}.heating.system.${currentHeatingSystem}.switchoverTemp`, 0);
        }

        if (h2kMinor >= 13 && type === "airHeatPump") {
            //Add "units" to object for 11.13 and onwards
            change(
                `${accessor}.cooling.system.airHeatPump.specifications.heatingEfficiency.unit`,
                manufacturer && modelId ? 1 : 0
            );
            change(
                `${accessor}.cooling.system.airHeatPump.specifications.coolingEfficiency.unit`,
                manufacturer && modelId ? 1 : 0
            );
        }

        // if (type === "airHeatPump") {
        //     change(`${accessor}.cooling.system.airHeatPump.performanceCurve`, {
        //         cooling: {
        //             highCooling: { temperature: 35, }, //95F
        //             lowCooling: { temperature: 27.8 }, //82F
        //         },
        //         heating: {
        //             highHeating: { temperature: 8.3,}, // 47F
        //             midHeating: { temperature: -8.3, }, // 17F
        //             lowHeating: { temperature: -15, }, // 5F
        //         },
        //     })
        // }
    };

    const activeCoolingSystem = typeContent(currentPrimarySystem);

    const systemTypes = [
        {
            value: "airConditioning",
            label: "Air Conditioning",
        },
        {
            value: "airHeatPump",
            label: "Air Source Heat Pump",
        },
        {
            value: "waterHeatPump",
            label: "Water Source Heat Pump",
        },
        {
            value: "groundHeatPump",
            label: "Ground Source Heat Pump",
        },
    ];

    const handleQuickCreate = (type) => {
        handleCreateSystem({
            type,
            manufacturer: false,
            modelId: false,
            favId: false,
            h2kMinor,
        });
        toggleOpen(false);
    };

    return (
        <div className={!isUpgrade && sharedClasses.section}>
            {!currentPrimarySystem && (
                <AddBox onClick={() => toggleOpen(true)} title="Create Cooling or Heat Pump System" icon="" />
            )}
            {currentPrimarySystem && (
                <div className={classes.heading}>
                    <div className={classes.systemsTitle}>
                        <h3>{activeCoolingSystem.name}</h3>
                        <Actions
                            type={currentPrimarySystem}
                            accessor={accessor}
                            change={change}
                            formName={formName}
                            removeFormErrors={removeFormErrors}
                        />
                    </div>
                    <Button className={classes.createButton} onClick={() => toggleOpen(true)} icon={AddIcon}>
                        Change Cooling or Heat Pump System
                    </Button>
                </div>
            )}
            <Drawer
                open={open}
                close={() => toggleOpen(false)}
                preTitle="New System"
                title={
                    currentPrimarySystem ? "Change Cooling or Heat Pump System" : "Create Cooling or Heat Pump System"
                }
                topBarColor="#0eb0a9"
            >
                <div>
                    <div style={{ marginBottom: "2rem" }}>
                        <h4 style={{ marginBottom: "1rem", fontSize: "1rem", fontWeight: 600 }}>
                            Create Default System
                        </h4>
                        <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#666" }}>
                            Choose a system type to create with default specifications. You can customize the
                            specifications after creation.
                        </p>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "1rem",
                            }}
                        >
                            {systemTypes.map((systemType) => (
                                <Button
                                    key={systemType.value}
                                    type="hollow"
                                    onClick={() => handleQuickCreate(systemType.value)}
                                    disabled={!canBeEditedFromParametric}
                                    icon={AddIcon}
                                    style={{
                                        textAlign: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {systemType.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <Accordion
                        heading="Optional: Import ENERGY STAR System"
                        initOpenState={false}
                        className={classes.energyStarAccordion}
                    >
                        <div>
                            <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#666" }}>
                                Import specifications from an ENERGY STAR certified system. This will replace the
                                default specifications.
                            </p>
                            <InputRow>
                                <Field
                                    component={Select}
                                    name={`${accessor}.cooling.selected.type`}
                                    options={[
                                        {
                                            value: false,
                                            label: "None",
                                        },
                                        ...systemTypes,
                                    ]}
                                    label="System Type"
                                    placeholder="Choose System Type"
                                    validate={primaryCoolingValidation}
                                    info={'"Type 2" system in HOT2000'}
                                    onChange={() => {
                                        change(`${accessor}.cooling.selected.eStar.manufacturer`, false);
                                        change(`${accessor}.cooling.selected.eStar.modelId`, false);
                                    }}
                                    disabled={!canBeEditedFromParametric}
                                />
                            </InputRow>
                            <InputRow>
                                <Field
                                    component={Select}
                                    name={`${accessor}.cooling.selected.eStar.manufacturer`}
                                    options={[
                                        {
                                            value: false,
                                            label: "None",
                                        },
                                        ...getEStarManufacturerOptions(primarySystemType),
                                    ]}
                                    label="ENERGY STAR Manufacturer"
                                    placeholder="Choose Manufacturer"
                                    validate={eStarManufacturerValidation}
                                    disabled={!primarySystemType || !canBeEditedFromParametric}
                                    info={"Select a manufacturer of an ENERGY STAR cooling system or heat pump"}
                                    search={true}
                                />
                            </InputRow>
                            <InputRow>
                                <Field
                                    component={Select}
                                    name={`${accessor}.cooling.selected.eStar.modelId`}
                                    options={[
                                        {
                                            value: false,
                                            label: "None",
                                        },
                                        ...getEStarModelOptions({
                                            systemType: primarySystemType,
                                            manufacturer: eStarManufacturerValue,
                                        }),
                                    ]}
                                    label="ENERGY STAR Model"
                                    placeholder="Choose Model"
                                    validate={eStarModelValidation}
                                    disabled={
                                        !primarySystemType || !eStarManufacturerValue || !canBeEditedFromParametric
                                    }
                                    info={"Select an ENERGY STAR certified model"}
                                    search={true}
                                />
                            </InputRow>
                            <div
                                className={classes.buttons}
                                style={{
                                    marginTop: "1.5rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "1.25rem",
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        handleCreateSystem({
                                            type: primarySystemType,
                                            manufacturer: eStarManufacturerValue,
                                            modelId: eStarModelId,
                                            h2kMinor,
                                        });
                                        toggleOpen(false);
                                    }}
                                    disabled={
                                        !primarySystemType ||
                                        !eStarManufacturerValue ||
                                        !eStarModelId ||
                                        !canBeEditedFromParametric
                                    }
                                >
                                    Import ENERGY STAR System
                                </Button>
                            </div>
                        </div>
                    </Accordion>

                    <div
                        className={classes.buttons}
                        style={{ marginTop: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}
                    >
                        <Button large type="hollow" onClick={() => toggleOpen(false)}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </Drawer>
            {currentPrimarySystem && (
                <activeCoolingSystem.Component
                    accessor={`${accessor}.cooling`}
                    formName={formName}
                    change={change}
                    isParametricAnalysis={isParametricAnalysis}
                    isUpgrade={isUpgrade}
                />
            )}
            <FansAndPumps accessor={`${accessor}.cooling`} formName={formName} change={change} />
            <h4 style={{ marginBottom: "1.25rem", marginTop: "1.75rem" }}>Cooling Season</h4>
            <InputRow gridTemplate="1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.cooling.season.start`}
                    options={getOptions({ fieldKey: "coolingSeasonStart" })}
                    label="Start Month"
                    placeholder="Choose Start Month"
                    validate={seasonStartValidation}
                    info={"For EnerGuide compliance leave at the default value (January)."}
                    disabled={!canBeEditedFromParametric}
                />
                <Field
                    component={Select}
                    name={`${accessor}.cooling.season.end`}
                    options={getOptions({ fieldKey: "coolingSeasonEnd" })}
                    label="End Month"
                    placeholder="Choose End Month"
                    validate={seasonEndValidation}
                    info={"For EnerGuide compliance leave at the default value (December)."}
                    disabled={!canBeEditedFromParametric}
                />
                <Field
                    component={Select}
                    name={`${accessor}.cooling.season.design`}
                    options={getOptions({ fieldKey: "coolingSeasonDesign" })}
                    label="Design Month"
                    placeholder="Choose Design Month"
                    validate={seasonDesignValidation}
                    info={"For EnerGuide compliance leave at the default value (July)."}
                    disabled={!canBeEditedFromParametric}
                />
            </InputRow>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

const mapStateToProps = createStructuredSelector({});

export default connect(mapStateToProps, mapDispatchToProps)(CoolingHeatPumps);
